/**
 * Background Location Tracking Service
 * Tracks user location 24/7 as long as the PWA is installed.
 * - Foreground: Uses watchPosition for continuous tracking
 * - Background: Uses Service Worker periodic sync + IndexedDB buffer
 * - Offline: Stores locations locally, syncs when online
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SEND_INTERVAL = 5 * 60 * 1000; // 5 minutes
const LOCATION_DB = 'destiny-location-db';
const LOCATION_STORE = 'pending-locations';

// IndexedDB helpers
function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(LOCATION_DB, 1);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(LOCATION_STORE)) {
        db.createObjectStore(LOCATION_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveToIDB(location) {
  const db = await openDB();
  const tx = db.transaction(LOCATION_STORE, 'readwrite');
  tx.objectStore(LOCATION_STORE).add(location);
  return new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

async function getAllFromIDB() {
  const db = await openDB();
  const tx = db.transaction(LOCATION_STORE, 'readonly');
  return new Promise((resolve, reject) => {
    const req = tx.objectStore(LOCATION_STORE).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function clearIDB() {
  const db = await openDB();
  const tx = db.transaction(LOCATION_STORE, 'readwrite');
  tx.objectStore(LOCATION_STORE).clear();
  return new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

class BackgroundLocationTracker {
  constructor() {
    this.watchId = null;
    this.sendTimer = null;
    this.isTracking = false;
    this.lastPosition = null;
    this.buffer = [];
    this.onStatusChange = null;
    this.onLocationUpdate = null;
  }

  getToken() {
    return localStorage.getItem('session_token');
  }

  async start() {
    if (this.isTracking) return;

    // Check if geolocation available
    if (!navigator.geolocation) {
      console.warn('[LocationTracker] Geolocation not supported');
      return false;
    }

    // Request permission
    try {
      const perm = await navigator.permissions.query({ name: 'geolocation' });
      if (perm.state === 'denied') {
        console.warn('[LocationTracker] Location permission denied');
        return false;
      }
    } catch (e) {
      // Some browsers don't support permissions API, continue anyway
    }

    this.isTracking = true;
    this._notifyStatus('tracking');

    // Start watching position
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => this._onPosition(pos),
      (err) => this._onError(err),
      {
        enableHighAccuracy: true,
        maximumAge: 60000, // Accept 1 min old position
        timeout: 30000
      }
    );

    // Periodic send to server
    this.sendTimer = setInterval(() => this._syncToServer(), SEND_INTERVAL);

    // Register periodic background sync
    await this._registerPeriodicSync();

    // Pass auth token to service worker
    this._sendTokenToSW();

    // Listen for SW messages
    this._setupSWListener();

    console.log('[LocationTracker] Started 24/7 tracking');
    return true;
  }

  stop() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    if (this.sendTimer) {
      clearInterval(this.sendTimer);
      this.sendTimer = null;
    }
    this.isTracking = false;
    this._notifyStatus('stopped');
    console.log('[LocationTracker] Stopped tracking');
  }

  _onPosition(pos) {
    const location = {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      timestamp: new Date(pos.timestamp).toISOString()
    };

    this.lastPosition = location;

    // Buffer the location
    this.buffer.push(location);

    // Also save to IndexedDB (for offline/background recovery)
    saveToIDB(location).catch(err => console.warn('[LocationTracker] IDB save failed:', err));

    // Notify listeners
    if (this.onLocationUpdate) {
      this.onLocationUpdate(location);
    }

    // Send immediately if buffer has enough points or first position
    if (this.buffer.length === 1) {
      this._sendSingleLocation(location);
    }
  }

  _onError(err) {
    console.warn('[LocationTracker] Geolocation error:', err.message);
    if (err.code === 1) {
      // Permission denied
      this._notifyStatus('denied');
      this.stop();
    }
  }

  async _sendSingleLocation(location) {
    const token = this.getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/location/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        })
      });
      if (response.ok) {
        const data = await response.json();
        // Dispatch geofence status event for GeofenceMonitor component
        if (data.geofence) {
          window.dispatchEvent(new CustomEvent('geofence-update', { detail: data.geofence }));
        }
      }
    } catch (err) {
      // Will be synced later via batch
      console.warn('[LocationTracker] Single send failed, will retry via batch');
    }
  }

  async _syncToServer() {
    const token = this.getToken();
    if (!token) return;

    // Get all pending from IDB
    let pending = [];
    try {
      pending = await getAllFromIDB();
    } catch (e) {
      pending = [...this.buffer];
    }

    if (pending.length === 0) return;

    try {
      const response = await fetch(`${API_URL}/api/location/batch-update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          locations: pending.map(p => ({
            latitude: p.latitude,
            longitude: p.longitude,
            accuracy: p.accuracy,
            timestamp: p.timestamp
          }))
        })
      });

      if (response.ok) {
        // Clear synced data
        await clearIDB().catch(() => {});
        this.buffer = [];
        this._notifyStatus('synced');
        console.log(`[LocationTracker] Synced ${pending.length} locations`);
      }
    } catch (err) {
      console.warn('[LocationTracker] Batch sync failed:', err);
      this._notifyStatus('offline');
    }
  }

  async _registerPeriodicSync() {
    try {
      const reg = await navigator.serviceWorker.ready;

      // Register periodic sync if available
      if ('periodicSync' in reg) {
        const status = await navigator.permissions.query({ name: 'periodic-background-sync' });
        if (status.state === 'granted') {
          await reg.periodicSync.register('location-tracking', {
            minInterval: 15 * 60 * 1000 // 15 minutes
          });
          console.log('[LocationTracker] Periodic sync registered');
        }
      }

      // Also register one-time sync as fallback
      if ('sync' in reg) {
        await reg.sync.register('sync-location');
      }
    } catch (err) {
      console.warn('[LocationTracker] Periodic sync registration failed:', err);
    }
  }

  _sendTokenToSW() {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SET_AUTH_TOKEN',
        data: {
          token: this.getToken(),
          apiUrl: API_URL
        }
      });
    }
  }

  _setupSWListener() {
    navigator.serviceWorker.addEventListener('message', (event) => {
      const { type } = event.data || {};
      if (type === 'REQUEST_LOCATION' && this.lastPosition) {
        // SW requested location, send latest
        this._sendSingleLocation(this.lastPosition);
      }
      if (type === 'LOCATION_SYNCED') {
        this._notifyStatus('synced');
      }
    });
  }

  _notifyStatus(status) {
    if (this.onStatusChange) {
      this.onStatusChange(status);
    }
  }

  getStatus() {
    if (!this.isTracking) return 'stopped';
    if (!this.lastPosition) return 'waiting';
    return 'tracking';
  }

  getLastPosition() {
    return this.lastPosition;
  }
}

// Singleton instance
const locationTracker = new BackgroundLocationTracker();
export default locationTracker;
